import { supabase } from '../lib/supabase';

export const laseaUserService = {
  /**
   * Get current LaSea user profile
   */
  async getCurrentUser() {
    // Use getSession() instead of getUser() to check for active session
    const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();

    if (sessionError) throw sessionError;
    if (!session) throw new Error('Auth session missing!');

    const { data, error } = await supabase
      ?.from('lasea_users')
      ?.select('*')
      ?.eq('id', session?.user?.id)
      ?.single();

    if (error) throw error;

    // Convert to camelCase
    return {
      id: data?.id,
      email: data?.email,
      fullName: data?.full_name,
      role: data?.role,
      companyName: data?.company_name,
      clientImage: data?.client_image,
      phone: data?.phone,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Get all customers (admin only)
   */
  async getAllCustomers() {
    const { data, error } = await supabase?.from('lasea_users')?.select('*')?.eq('role', 'customer')?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching customers:', error);
      return [];
    }

    // Convert to camelCase
    return (data || []).map(row => ({
      id: row?.id,
      email: row?.email,
      fullName: row?.full_name,
      role: row?.role,
      companyName: row?.company_name,
      clientImage: row?.client_image,
      phone: row?.phone,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    }));
  },

  /**
   * Get all movers (admin only)
   */
  async getAllMovers() {
    try {
      const { data, error } = await supabase?.from('lasea_users')?.select('*')?.eq('role', 'mover')?.order('created_at', { ascending: false });

      if (error) {
        // Log but don't crash
        console.warn('Warning: Could not fetch movers. This might be due to RLS or schema mismatch.', error.message);
        return [];
      }
      return (data || []).map(row => ({
        id: row?.id,
        email: row?.email,
        fullName: row?.full_name,
        role: row?.role,
        companyName: row?.company_name,
        clientImage: row?.client_image,
        phone: row?.phone,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at
      }));
    } catch (error) {
      console.error('Unexpected error in getAllMovers:', error);
      return [];
    }
  },

  /**
   * Helper to validate UUID
   */
  isValidUUID(uuid) {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  },

  /**
   * Get user profile with validation
   */
  async getUserProfile(userId) {
    if (!this.isValidUUID(userId)) {
      console.error('Invalid UUID provided to getUserProfile:', userId);
      throw new Error('Invalid User ID');
    }
    // ... rest of logic


    // Convert to camelCase
    return (data || []).map(row => ({
      id: row?.id,
      email: row?.email,
      fullName: row?.full_name,
      role: row?.role,
      companyName: row?.company_name,
      clientImage: row?.client_image,
      phone: row?.phone,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    }));
  },

  /**
   * Update user profile
   */
  async updateProfile(userId, updates) {
    // Convert camelCase to snake_case
    const dbUpdates = {};
    if (updates?.fullName !== undefined) dbUpdates.full_name = updates?.fullName;
    if (updates?.companyName !== undefined) dbUpdates.company_name = updates?.companyName;
    if (updates?.phone !== undefined) dbUpdates.phone = updates?.phone;
    if (updates?.clientImage !== undefined) dbUpdates.client_image = updates?.clientImage;

    const { data, error } = await supabase?.from('lasea_users')?.update(dbUpdates)?.eq('id', userId)?.select()?.single();

    if (error) throw error;

    // Convert back to camelCase
    return {
      id: data?.id,
      email: data?.email,
      fullName: data?.full_name,
      role: data?.role,
      companyName: data?.company_name,
      clientImage: data?.client_image,
      phone: data?.phone,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Check if current user is admin
   */
  async isAdmin() {
    try {
      const user = await this.getCurrentUser();
      return user?.role === 'admin';
    } catch (error) {
      return false;
    }
  },

  /**
   * Upload profile image to storage
   * @param {File} file - Image file to upload
   * @param {string} userId - UUID of the user
   * @returns {string} Public URL of uploaded file
   */
  async uploadProfileImage(file, userId) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `profiles/${userId}/${Date.now()}.${fileExt}`;

      const WORKER_URL = import.meta.env.VITE_WORKER_URL;
      const WORKER_SECRET = import.meta.env.VITE_WORKER_SECRET;

      if (WORKER_URL) {
        // Use Worker Proxy
        let baseUrl = WORKER_URL.startsWith('ttps://') ? 'h' + WORKER_URL : WORKER_URL;
        if (baseUrl && !baseUrl.startsWith('http')) baseUrl = 'https://' + baseUrl;

        const response = await fetch(`${baseUrl}/upload/${fileName}`, {
          method: 'POST',
          headers: {
            'X-Worker-Auth': WORKER_SECRET
          },
          body: file
        });

        if (!response.ok) throw new Error('Worker upload failed');
        const result = await response.json();
        
        // Use neonStorage or generate signed URL for the worker uploaded path
        // Since the worker doesn't serve the file directly with a GET yet, 
        // we can still fall back to getting the signedUrl via S3Client (which works if credentials are set)
        // OR return the signed URL from the proxy if possible.
        // Actually, we must use `createSignedUrl` from supabase.storage!
        const { data: stringUrlData, error: urlError } = await supabase?.storage
          ?.from('lasea-inventory-photos')
          ?.createSignedUrl(result.path || fileName, 315360000); // 10 years expiry
          
        if (urlError) throw urlError;
        return stringUrlData?.signedUrl;
      }

      // Direct R2 Upload
      const { data, error } = await supabase?.storage
        ?.from('lasea-inventory-photos')
        ?.upload(fileName, file, {
          upsert: true
        });

      if (error) throw error;

      // Get signed URL (since bucket might be private)
      const { data: stringUrlData, error: urlError } = await supabase?.storage
        ?.from('lasea-inventory-photos')
        ?.createSignedUrl(data?.path, 315360000); // 10 years expiry (long lived)

      if (urlError) throw urlError;

      return stringUrlData?.signedUrl;
    } catch (error) {
      console.error('Error uploading profile image:', error);
      throw error;
    }
  },

  /**
   * Create a new user account (Client or Mover)
   * @param {Object} formData - User data { email, password, fullName, companyName, phone }
   * @param {string} role - 'customer' or 'mover'
   */
  async createAccount(formData, role) {
    try {
      const { email, password, fullName, companyName, phone } = formData;

      // Sign up the user
      // Note: This will normally log in the new user immediately in client-side Supabase.
      // If we want to avoid this, we'd need a backend function.
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            company_name: companyName || '',
            phone: phone || '',
            role: role, // 'customer' or 'mover'
            project: 'lasea'
          }
        }
      });

      if (error) throw error;

      // Manually insert into lasea_users table if the auth user was created
      if (data?.user?.id) {
        const { error: dbError } = await supabase.from('lasea_users').insert([{
          id: data.user.id,
          email: email,
          full_name: fullName,
          company_name: companyName || '',
          phone: phone || '',
          role: role
        }]);

        if (dbError) {
          console.error('Error creating lasea_users record:', dbError);
          // Don't throw just log so we don't break the flow (since account is still made), 
          // but we should attempt to fix dbError if it throws, so we keep the throw for trace
          throw new Error(`Database error saving new user: ${dbError.message}`);
        }
      }

      return data;
    } catch (error) {
      console.error('Error creating account:', error);
      throw error;
    }
  }
};